import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import axiosInstance from "../../../api/axiosInstance";
import AddPurchaseOrder from "./AddPurchaseOrder";
import ViewPurchaseOrder from "./ViewPurchaseOrder";

// ─── Theme (matches Pharmacy.jsx) ────────────────────────────────────────────
const C = { gray:'#5B6B79', blue:'#96C5F7', red:'#9E2B25', orange:'#F29E4C', green:'#2CA87F', teal:'#0891b2', purple:'#7c3aed' }
const thStyle = { fontSize:11, fontWeight:700, color:C.gray, textTransform:'uppercase', letterSpacing:'0.05em', padding:'8px 10px', background:'#f8f9fb', borderBottom:'1px solid #e8ecf0', whiteSpace:'nowrap' }
const tdStyle = { fontSize:13, color:'#2d3748', padding:'7px 10px', verticalAlign:'middle', borderBottom:'1px solid #f0f2f5' }
const actionIcon = (color) => ({ width:30, height:30, borderRadius:8, border:'none', cursor:'pointer', background:`${color}22`, color, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:15, marginRight:4, flexShrink:0 })
const inputStyle = { width:'100%', height:36, border:'1.5px solid #dde3eb', borderRadius:8, padding:'0 10px', fontSize:13, color:'#1a202c', outline:'none', boxSizing:'border-box' }

const STATUS_META = {
  draft:              { label:'Draft',              bg:`${C.gray}18`,   color:C.gray   },
  sent:               { label:'Sent',               bg:`${C.teal}18`,   color:C.teal   },
  partially_received: { label:'Partial',            bg:`${C.orange}18`, color:C.orange },
  received:           { label:'Received',           bg:`${C.green}18`,  color:C.green  },
  cancelled:          { label:'Cancelled',          bg:`${C.red}18`,    color:C.red    },
};

export default function PurchaseOrders() {
  const [orders, setOrders]   = useState([]);
  const [search, setSearch]   = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [viewId, setViewId]   = useState(null);

  const fetchOrders = async () => {
    try {
      const res = await axiosInstance.get("getPurchaseOrders");
      setOrders(res.data.data || []);
    } catch {
      Swal.fire("Error", "Failed to load purchase orders", "error");
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const handleDelete = async (id) => {
    const ok = await Swal.fire({ title:"Delete PO?", text:"Only draft POs can be deleted.", icon:"warning", showCancelButton:true, confirmButtonText:"Yes, delete it!" });
    if (ok.isConfirmed) {
      try { await axiosInstance.delete(`deletePurchaseOrder/${id}`); Swal.fire("Deleted!", "Purchase order deleted.", "success"); fetchOrders(); }
      catch (err) { Swal.fire("Error", err?.response?.data?.message || "Something went wrong!", "error"); }
    }
  };

  const filtered = orders.filter(o =>
    o.po_number?.toLowerCase().includes(search.toLowerCase()) ||
    o.vendor_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (addOpen) return <AddPurchaseOrder onClose={() => { setAddOpen(false); fetchOrders(); }} />;
  if (viewId)  return <ViewPurchaseOrder id={viewId} onClose={() => { setViewId(null); fetchOrders(); }} />;

  return (
    <>
      {/* ── Toolbar ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:14 }}>
        <div style={{ position:'relative', maxWidth:340 }}>
          <i className="ti ti-search" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.gray, fontSize:14, pointerEvents:'none' }} />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by PO # or vendor..."
            style={{ ...inputStyle, paddingLeft:32, maxWidth:340 }}
          />
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span style={{ fontSize:11, color:C.gray }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
          <button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize:13, padding:'5px 12px' }} onClick={() => setAddOpen(true)}>
            <i className="ti ti-plus" style={{ fontSize:14 }} /> New Purchase Order
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ overflowX:'auto', borderRadius:8, border:'1px solid #e8ecf0' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, borderLeft:`3px solid ${C.orange}` }}>#</th>
              <th style={thStyle}>PO Number</th>
              <th style={thStyle}>Vendor</th>
              <th style={thStyle}>Order Date</th>
              <th style={thStyle}>Expected Date</th>
              <th style={thStyle}>Items</th>
              <th style={thStyle}>Total</th>
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, textAlign:'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ ...tdStyle, textAlign:'center', color:C.gray, padding:'36px 0' }}>
                  <i className="ti ti-database-off" style={{ fontSize:28, display:'block', marginBottom:6, opacity:0.4 }} />
                  No purchase orders found
                </td>
              </tr>
            ) : filtered.map((item, i) => {
              const s = STATUS_META[item.status] || { label: item.status, bg:`${C.gray}18`, color:C.gray };
              return (
                <tr
                  key={item.id}
                  style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}
                  onMouseEnter={e => e.currentTarget.style.background = `${C.orange}0d`}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
                >
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={{ ...tdStyle, fontWeight:600, fontFamily:'monospace', fontSize:12 }}>{item.po_number}</td>
                  <td style={tdStyle}>{item.vendor_name || '—'}</td>
                  <td style={tdStyle}>{item.order_date?.slice(0, 10) || '—'}</td>
                  <td style={tdStyle}>{item.expected_date?.slice(0, 10) || '—'}</td>
                  <td style={tdStyle}>{item.item_count ?? '—'}</td>
                  <td style={tdStyle}>{parseFloat(item.total_amount || 0).toFixed(3)}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:s.bg, color:s.color }}>{s.label}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign:'center', whiteSpace:'nowrap' }}>
                    <button onClick={() => setViewId(item.id)} style={actionIcon(C.blue)} title="View"><i className="ti ti-eye" /></button>
                    {item.status === 'draft' && (
                      <button onClick={() => handleDelete(item.id)} style={actionIcon(C.red)} title="Delete"><i className="ti ti-trash" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
